import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';

import { Button, Dialog, Message } from '@alifd/next';
import { Input, Table } from 'antd';
import { Box, Typography } from '@mui/material';
import ReactDragListView from 'react-drag-listview';
import Swal from 'sweetalert2';
import useService from '@/lib/hooks/useService';
import moment from 'moment';
import { ColorBlock, ColorContainer } from './styledComponents';
import { type Color, ColorPicker, createColor } from 'material-ui-color';
import type { InputRef, InputProps } from 'antd';
import type { ColumnsType } from 'antd/es/table/interface';
import type { CategoryMenu } from './types';
import type EditCategoriesProps from './types';

const EditCategories: React.FC<EditCategoriesProps> = ({
  categoryName,
  buttonLabel,
  dialogTitle,
  onUpdate,
}) => {
  const [visible, setVisible] = useState(false);
  const [categoryData, setCategoryData] = useState<CategoryMenu>();
  const [dataSource, setDataSource] = useState<
    { id: string; key?: string; name: string; color: string }[]
  >([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>();

  const [resData, updateCategoryData, categoryMenuClient] =
    useService<CategoryMenu>({
      collectionName: 'categoryMenu',
      query: {
        name: categoryName,
      },
    });
  const [addInput, setAddInput] = useState<{
    value: string | undefined;
    status: InputProps['status'];
    errorMessage?: string;
  }>({
    value: undefined,
    status: '',
  });
  const [editInput, setEditInput] = useState<typeof addInput>({
    value: undefined,
    status: '',
  });
  const [settingIndex, setSettingIndex] = useState<number | null>(null);
  const [pickedColor, setPickedColor] = useState<Color>(createColor('#fff'));

  const editInputRef = useRef<InputRef>(null);

  const validateCategory = useCallback(
    (inputValue: string) => {
      const nonSelectedCategories = dataSource.filter(
        (category) => category.id !== selectedCategoryId
      );
      if (
        nonSelectedCategories.find((category) => category.name === inputValue)
      ) {
        return false;
      }
      return true;
    },
    [dataSource, selectedCategoryId]
  );

  const changeData = useCallback(
    async (
      data: { id: string; name: string; color: string }[],
      manipulateText: string
    ) => {
      const newData: CategoryMenu['data'] = data.map((category) => ({
        id: category.id,
        name: category.name,
        color: category.color,
      }));
      try {
        await categoryMenuClient.patch(categoryData?._id, {
          data: newData,
        });
        Message.success(`${manipulateText}成功`);
        onUpdate?.();
        updateCategoryData();
        setEditInput({
          value: undefined,
          status: '',
        });
        setAddInput({
          value: undefined,
          status: '',
        });
        setSelectedCategoryId(undefined);
        setSettingIndex(null);
      } catch (error) {
        console.error(error);
        Message.error(`${manipulateText}失敗`);
      }
    },
    [categoryData?._id, categoryMenuClient, onUpdate, updateCategoryData]
  );

  const getHexColor = () => {
    const value = Array(4)
      .fill(0)
      .map(() =>
        Math.floor(Math.random() * 255)
          .toString(16)
          .padStart(2, '0')
      )
      .join('');
    return `#${value}`;
  };

  const handleAddCategory = useCallback(async () => {
    if (addInput.value) {
      const newCategory = {
        id: moment().valueOf().toString(),
        name: addInput.value,
        color: getHexColor(),
      };
      const newData = [...dataSource, newCategory];
      await changeData(newData, '新增');
    }
  }, [addInput.value, changeData, dataSource]);

  const handleEditCategory = useCallback(async () => {
    if (selectedCategoryId) {
      const newData = dataSource.map((category) => {
        if (category.id === selectedCategoryId && editInput.value) {
          return {
            ...category,
            name: editInput.value,
            color: `#${pickedColor.hex.toLowerCase()}`,
          };
        }
        return category;
      });
      await changeData(newData, '編輯');
    }
  }, [
    changeData,
    dataSource,
    editInput.value,
    pickedColor.hex,
    selectedCategoryId,
  ]);

  const handleDeleteCategory = useCallback(
    async (id: string) => {
      const result = await Swal.fire({
        title: '確定要刪除此分類嗎？',
        text: '此動作無法復原',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '確定',
        cancelButtonText: '取消',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
      });
      if (result.isConfirmed) {
        const newData = dataSource.filter((category) => category.id !== id);
        await changeData(newData, '刪除');
      }
    },
    [changeData, dataSource]
  );

  const handleDragEnd = useCallback(
    async (fromIndex: number, toIndex: number) => {
      const newData = [...dataSource];
      const [removed] = newData.splice(fromIndex - 1, 1);
      newData.splice(toIndex - 1, 0, removed);
      await changeData(newData, '排序');
    },
    [changeData, dataSource]
  );

  const handleEditInputChange: InputProps['onChange'] = useCallback(
    (e) => {
      const value = e.target.value;
      if (value) {
        const valid = validateCategory(value);
        if (valid) {
          setEditInput({
            value,
            status: '',
          });
        } else {
          setEditInput({
            value,
            status: 'error',
            errorMessage: '此類別已存在',
          });
        }
      } else {
        setEditInput({
          value,
          status: 'error',
          errorMessage: '請輸入類別名稱',
        });
      }
    },
    [validateCategory]
  );

  const handleCancelEditCategory = useCallback(() => {
    setSettingIndex(null);
    setSelectedCategoryId(undefined);
  }, []);

  const handleClose = useCallback(() => {
    setVisible(false);
    setAddInput({
      value: undefined,
      status: '',
    });
    setEditInput({
      value: undefined,
      status: '',
    });
    setSelectedCategoryId(undefined);
    handleCancelEditCategory();
  }, [handleCancelEditCategory]);

  useEffect(() => {
    if (editInputRef.current && selectedCategoryId) {
      editInputRef.current.focus();
    }
  }, [editInputRef, selectedCategoryId]);

  useEffect(() => {
    if (resData.length > 0) {
      setCategoryData(resData[0]);
      const newData = resData[0].data;
      setDataSource(newData);
    }
  }, [resData]);

  useEffect(() => {
    if (editInput.status === 'error' && editInput.errorMessage) {
      Message.error(editInput.errorMessage);
    }
  }, [editInput.status, editInput.errorMessage]);

  useEffect(() => {
    if (addInput.status === 'error' && addInput.errorMessage) {
      Message.error(addInput.errorMessage);
    }
  }, [addInput.status, addInput.errorMessage]);

  const columns: ColumnsType<typeof dataSource[0]> = useMemo(
    () => [
      {
        title: '類別名稱',
        dataIndex: 'name',
        key: 'name',
        width: '200px',
        render: (text, record) => {
          if (record.id === selectedCategoryId) {
            return (
              <Input
                value={editInput.value}
                status={editInput.status}
                onChange={handleEditInputChange}
                ref={editInputRef}
              />
            );
          }
          return text;
        },
      },
      {
        title: '顏色',
        dataIndex: 'color',
        width: '100px',
        render: (color: string, _: any, index: number) => {
          if (index === settingIndex) {
            return (
              <ColorContainer>
                <ColorPicker
                  hideTextfield
                  value={pickedColor}
                  onChange={(c) => {
                    setPickedColor(c);
                  }}
                />
              </ColorContainer>
            );
          } else {
            return (
              <ColorContainer>
                <ColorBlock color={color} />
              </ColorContainer>
            );
          }
        },
      },
      {
        title: '操作',
        key: 'action',
        width: '200px',
        render: (_, record, index) => {
          return (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-around',
              }}
            >
              {record.id === selectedCategoryId && (
                <>
                  <Button
                    type="primary"
                    disabled={editInput.status === 'error'}
                    onClick={handleEditCategory}
                  >
                    確定
                  </Button>
                  <Button onClick={handleCancelEditCategory}>取消</Button>
                </>
              )}
              {record.id !== selectedCategoryId && (
                <>
                  <Button
                    onClick={() => {
                      setEditInput({
                        value: record.name,
                        status: '',
                      });
                      setAddInput({
                        value: undefined,
                        status: '',
                      });
                      setSelectedCategoryId(record.id);
                      setSettingIndex(index);
                      setPickedColor(createColor(record.color));
                    }}
                  >
                    編輯
                  </Button>
                  <Button
                    type="normal"
                    warning
                    onClick={() => handleDeleteCategory(record.id)}
                  >
                    刪除
                  </Button>
                </>
              )}
            </Box>
          );
        },
      },
    ],
    [
      editInput.status,
      editInput.value,
      handleCancelEditCategory,
      handleDeleteCategory,
      handleEditCategory,
      handleEditInputChange,
      pickedColor,
      selectedCategoryId,
      settingIndex,
    ]
  );

  return (
    <>
      <Button onClick={() => setVisible(true)}>{buttonLabel}</Button>
      <Dialog
        title={dialogTitle}
        visible={visible}
        onClose={handleClose}
        onCancel={handleClose}
        cancelProps={{ children: '關閉' }}
        footerActions={['cancel']}
      >
        <Box
          sx={{
            width: '520px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: '10px',
            }}
          >
            <Input
              placeholder="新增類別"
              status={addInput.status}
              value={addInput.value}
              onChange={(e) => {
                const value = e.target.value;
                const valid = validateCategory(value);
                if (valid) {
                  setAddInput({
                    value,
                    status: '',
                  });
                } else {
                  setAddInput({
                    value,
                    status: 'error',
                    errorMessage: '此類別已存在',
                  });
                }
              }}
              onFocus={() => setSelectedCategoryId(undefined)}
            />
            <Button
              disabled={addInput.status === 'error' || !addInput.value}
              onClick={handleAddCategory}
            >
              新增
            </Button>
          </Box>
          <Typography variant="subtitle2">拖曳項目以調整顯示順序</Typography>
          <ReactDragListView onDragEnd={handleDragEnd}>
            <Table
              bordered
              dataSource={dataSource}
              columns={columns}
              pagination={false}
              scroll={{ y: 400, scrollToFirstRowOnChange: true }}
              rowKey={(record) => record.id}
            />
          </ReactDragListView>
        </Box>
      </Dialog>
    </>
  );
};

export default React.memo(EditCategories);
